
import { useFormContext } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { RHFAutocomplete } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
// types
import { LoSam } from 'src/types/losam';

// ----------------------------------------------------------------------

type Props = {
  currentLoSam?: LoSam;
  sensorModels: any[]; // Or define specific type if available
};

export default function LoSamSensorForm({ currentLoSam, sensorModels }: Props) {
  const mdUp = useResponsive('up', 'md');
  const { watch, setValue } = useFormContext();
  const values = watch();

  return (
    <>
      {mdUp && (
        <Grid md={3}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Thiết bị cảm biến
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Các thiết bị cảm biến nhiệt độ, độ ẩm
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={9}>
        <Card>
          {!mdUp && <CardHeader title="Thiết bị cảm biến" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <RHFAutocomplete
              name="sensorIds"
              label="Chọn thiết bị cảm biến"
              placeholder="+ Thiết bị"
              multiple
              freeSolo={false}
              options={sensorModels || []}
              getOptionLabel={(option) => {
                if (!option) return '';
                return typeof option === 'object'
                  ? `${option.sensorCode} - ${option.jValue} (${option.unit})`
                  : String(option);
              }}
              isOptionEqualToValue={(option, value) =>
                typeof option === 'object' && typeof value === 'object'
                  ? option.sensorId === value.sensorId
                  : option === value
              }
              // Map sensorIds sang objects khi edit
              value={
                values.sensorIds?.map((id: number) =>
                  sensorModels?.find((s) => s.sensorId === id)
                ).filter(Boolean) || []
              }
              renderOption={(props, option) => (
                <li {...props} key={option?.sensorId}>
                  <Iconify icon="mdi:sensor" width={24} sx={{ mr: 1 }} />
                  <Stack>
                    <Typography variant="body2">
                      {option?.sensorCode} - {option?.jValue}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Đơn vị: {option?.unit}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={typeof option === 'object' ? option.sensorId : index}
                    label={
                      typeof option === 'object'
                        ? `${option.sensorCode} - ${option.jValue}`
                        : option
                    }
                    size="small"
                    color="primary"
                    variant="soft"
                  />
                ))
              }
              onChange={(event, newValue) => {
                // Chuyển đổi từ object sang array of IDs
                const ids = newValue
                  .map((item) => (typeof item === 'object' ? item.sensorId : item))
                  .filter((id): id is number => typeof id === 'number');
                setValue('sensorIds', ids);
              }}
            />

            {/* Hiển thị danh sách các thiết bị đã chọn */}
            {values.sensorIds && values.sensorIds.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Thiết bị đã chọn ({values.sensorIds.length})
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>
      </Grid>
    </>
  );
}
